import {
  ShiftManagementBulkDeleteRestDaysDocument,
  ShiftManagementBulkDeleteShiftsDocument,
} from 'generated/resources/graphql'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { trackDeleteByRangeEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'

interface Props {
  startDate: string
  endDate: string
  employeeIds: number[]
  destroyBackupShifts: boolean
  destroyAutomaticOnly: boolean
}

const useDestroyByRange = ({
  startDate,
  endDate,
  employeeIds,
  destroyBackupShifts,
  destroyAutomaticOnly,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }] = useSchedule()
  const { refreshShiftsRows } = useRefreshShiftsRows()
  const restDayFlag = hasFeatureFlag('shift_management_rest_day')

  const startDateParam = dateUIToBackend(startDate)
  const endDateParam = dateUIToBackend(endDate)

  const [destroyShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
  })

  const [destroyRestDays] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
  })

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const result = await destroyShifts({
        variables: {
          startAt: startDateParam,
          endAt: endDateParam,
          employeeIds,
          destroyBackupShifts: destroyBackupShifts,
          destroyAutomaticShifts: destroyAutomaticOnly,
        },
        update: (cache, { data }) => {
          data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
            cache.evict({
              id: cache.identify(shift),
            })
          })
        },
      })

      const handled = handleResult(result, (_errors) => {})

      const shiftsData = handled.success ? handled.data?.shifts || [] : []
      const deletedShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId || 0,
        })
      )

      const deleteRestDays = async () => {
        if (!restDayFlag) return []
        const restDaysResult = await destroyRestDays({
          variables: {
            startAt: startDateParam,
            endAt: endDateParam,
            employeeIds,
          },
          update: (cache, { data }) => {
            data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays?.forEach(
              (restDay) => {
                cache.evict({
                  id: cache.identify(restDay),
                })
              }
            )
          },
        })

        const handledRestDays = handleResult(restDaysResult, (_errors) => {})
        const restDays = handledRestDays.success ? handledRestDays.data?.restDays || [] : []
        return restDays.map((restDay) => {
          return RestDay.build({
            id: restDay.id,
            employee_id: restDay.employee?.id || 0,
            date: restDay.date,
            state: restDay.state,
            timezone: restDay.timezone || '',
            employeeFullName: restDay.employee?.fullName || '',
            employeeAvatar: restDay.employee?.avatar?.url || '',
          })
        })
      }

      const deletedRestDays = await deleteRestDays()

      trackDeleteByRangeEvent(
        deletedShifts,
        deletedRestDays,
        view,
        startDateParam,
        endDateParam,
        destroyAutomaticOnly
      )

      await refreshShiftsRows(shiftsRows, [])

      closePortal()
    },
    {
      errorFlash: true,
      successMessage: i18n.t('shift_management.actions.delete_shifts_success'),
    }
  )

  return {
    onBulkDestroyShifts: performAction,
    submitting,
  }
}

export default useDestroyByRange
